import {Box, Button, createStyles, Group, Loader, Stack, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useCallback} from 'react';

import {RoutineExecutionStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import fuzzydate from 'util/fuzzydate';

import {TriggerType} from './RoutineTypes';
import useRoutineExecutions, {RoutineExecution} from './useRoutineExecutions';

const useStyles = createStyles((theme) => ({
  row: {
    display: 'block',
    width: '100%',
    padding: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    textAlign: 'left',
  },
  clickableRow: {
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  icon: {
    color: theme.colors.gray[6],
    marginTop: 2,
  },
  warningIcon: {
    color: theme.colors.orange[7],
  },
  timestamp: {
    whiteSpace: 'nowrap',
  },
}));

function getFailureReason(status: RoutineExecutionStatus): string {
  switch (status) {
    case RoutineExecutionStatus.FAILED:
      return 'The run failed before it could be dispatched.';
    case RoutineExecutionStatus.RATE_LIMITED:
      return 'The run was rate limited.';
    case RoutineExecutionStatus.PENDING:
      return 'The run has not been dispatched yet.';
    default:
      return 'The run was not dispatched.';
  }
}

function getTriggerLabel(triggerType: TriggerType): string {
  return triggerType === TriggerType.EVENT_BASED ? 'Event based' : 'Time based';
}

interface RunRowProps {
  run: RoutineExecution;
  routineName: string;
  triggerType: TriggerType;
}

function RunRow({run, routineName, triggerType}: RunRowProps): JSX.Element {
  const {classes, cx} = useStyles();
  const chatSessionId = run.chatSession?.id ?? null;
  const wasDispatched = run.status === RoutineExecutionStatus.DISPATCHED;
  const canViewChat = wasDispatched && chatSessionId != null && APIUtil.isChatSession(chatSessionId);

  const handleOpenChat = useCallback(() => {
    if (chatSessionId != null) {
      openChat(chatSessionId);
    }
  }, [chatSessionId]);

  const content = (
    <Group position="apart" noWrap={true} align="flex-start" spacing="sm">
      <Group spacing="sm" noWrap={true} align="flex-start">
        {wasDispatched ? (
          <BenchlingIcon icon="comment-outline" className={classes.icon} />
        ) : (
          <Tooltip label={getFailureReason(run.status)} multiline={true} withArrow={true}>
            <Box component="span" role="img" aria-label="Run warning">
              <BenchlingIcon icon="exclamation-triangle" className={cx(classes.icon, classes.warningIcon)} />
            </Box>
          </Tooltip>
        )}
        <Stack spacing={0}>
          <Text size="sm" fw={500} lineClamp={1}>
            {routineName}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {getTriggerLabel(triggerType)}
          </Text>
        </Stack>
      </Group>
      <Text size="xs" c="dimmed" className={classes.timestamp}>
        {fuzzydate(run.createdAt, {compact: true})}
      </Text>
    </Group>
  );

  if (canViewChat) {
    return (
      <UnstyledButton className={cx(classes.row, classes.clickableRow)} onClick={handleOpenChat}>
        {content}
      </UnstyledButton>
    );
  }
  return <Box className={classes.row}>{content}</Box>;
}

interface RoutineRunsTabProps {
  routineId: string;
  routineName: string;
  triggerType: TriggerType;
}

interface RoutineRunsListProps {
  runs: ReadonlyArray<RoutineExecution>;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
  routineName: string;
  triggerType: TriggerType;
}

export function RoutineRunsList({
  runs,
  isLoading,
  isError,
  hasMore,
  loadMore,
  routineName,
  triggerType,
}: RoutineRunsListProps): JSX.Element {
  if (isLoading && runs.length === 0) {
    return (
      <Group position="center" py="lg">
        <Loader size="sm" />
      </Group>
    );
  }

  if (isError && runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Failed to load runs. Please try again.
      </Text>
    );
  }

  if (runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        This agent hasn't run yet.
      </Text>
    );
  }

  return (
    <Stack spacing="md">
      <Stack spacing={4}>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} routineName={routineName} triggerType={triggerType} />
        ))}
      </Stack>
      {hasMore && (
        <Group position="center">
          <Button variant="ghost" size="sm" onClick={loadMore}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}

export default function RoutineRunsTab({
  routineId,
  routineName,
  triggerType,
}: RoutineRunsTabProps): JSX.Element {
  const {runs, isLoading, isError, hasMore, loadMore} = useRoutineExecutions({routineId});

  return (
    <RoutineRunsList
      runs={runs}
      isLoading={isLoading}
      isError={isError}
      hasMore={hasMore}
      loadMore={loadMore}
      routineName={routineName}
      triggerType={triggerType}
    />
  );
}
